import Cookies from 'js-cookie';

const CONFIG_COOKIE = 'hf_config';
const MAX_AGE_DAYS = 365;

/** Reports-page filter bar. Empty string means "never set" — the page falls back to its own default. */
export interface ReportFiltersConfig {
  fundId: string;
  status: string;
  fromMonth: string;
  toMonth: string;
}

/**
 * Sticky UI settings that a page needs *synchronously* on its first render, before the async
 * user prefs resolve. Mirrored from `UserPreferences`; add new sticky settings as fields here.
 */
export interface ConfigSettings {
  reportFilters: ReportFiltersConfig;
}

function cookieBase(): Cookies.CookieAttributes {
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  return { sameSite: 'strict', path: '/', ...(secure ? { secure: true } : {}) };
}

export function defaultConfigSettings(): ConfigSettings {
  return { reportFilters: { fundId: '', status: '', fromMonth: '', toMonth: '' } };
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export function sanitizeConfigSettings(value: unknown): ConfigSettings {
  if (!value || typeof value !== 'object') return defaultConfigSettings();
  const raw = (value as { reportFilters?: unknown }).reportFilters;
  if (!raw || typeof raw !== 'object') return defaultConfigSettings();
  const filters = raw as Record<string, unknown>;
  return {
    reportFilters: {
      fundId: asString(filters.fundId),
      status: asString(filters.status),
      fromMonth: asString(filters.fromMonth),
      toMonth: asString(filters.toMonth),
    },
  };
}

export function readConfigSettings(): ConfigSettings {
  const raw = Cookies.get(CONFIG_COOKIE);
  if (!raw) return defaultConfigSettings();
  try {
    return sanitizeConfigSettings(JSON.parse(raw));
  } catch {
    return defaultConfigSettings();
  }
}

export function writeConfigSettings(value: ConfigSettings): void {
  Cookies.set(CONFIG_COOKIE, JSON.stringify(value), { ...cookieBase(), expires: MAX_AGE_DAYS });
}
